/**
 * useStatementFigure — la figure d'un énoncé que le serveur dessine encore.
 *
 * Le moteur de `MaterialContentDisplay` (legacy) : quand le support annonce une figure
 * (`figure_description`) qui n'est pas encore dans son texte (`![figure](…)`), ni en SVG, le serveur
 * la génère quelques secondes après l'énoncé. On relit le support (`getMaterial`) toutes les 5 s,
 * 60 s au plus, et dès que la figure est là, l'énoncé frais remplace l'ancien. Au-delà, ou dès que
 * le serveur refuse la relecture (403, 404… : un refus se répète, `isRetryableFailure`), on cesse
 * d'attendre et de l'annoncer.
 */
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../data/services/activitiesService"
import { apiCall, type ApiService } from "../shared/apiCall"
import { isRetryableFailure } from "../shared/apiFailure"
import { createTelemetry, httpStatusOf } from "../shared/telemetry"

export const FIGURE_POLL_INTERVAL_MS = 5000
export const FIGURE_POLL_TIMEOUT_MS = 60_000

const FIGURE_IMAGE_MARK = "![figure]("

export type StatementMaterial = {
    id?: number | string | null
    text?: string | null
    figure_description?: string | null
    figure_svg?: string | null
} & Record<string, unknown>

/** La figure est annoncée, et pas encore là : pure. */
export function awaitsFigure(material: StatementMaterial | null | undefined): boolean {
    return (
        material?.id != null &&
        !!material.figure_description &&
        !material.figure_svg &&
        !material.text?.includes(FIGURE_IMAGE_MARK)
    )
}

/** La figure est arrivée (dans le texte, ou en SVG). */
export function hasFigure(material: StatementMaterial | null | undefined): boolean {
    return !!material?.text?.includes(FIGURE_IMAGE_MARK) || !!material?.figure_svg
}

type Dispatch = (action: never) => unknown

const telemetry = createTelemetry("activity.statement", { role: "student" })

export function useStatementFigure<M extends StatementMaterial>(
    material: M | null | undefined
): { material: M | null; figurePending: boolean } {
    const dispatch = useDispatch() as unknown as Dispatch
    const [fresh, setFresh] = useState<M | null>(null)
    const [waitOver, setWaitOver] = useState(false)
    const materialId = material?.id ?? null
    const displayed = fresh != null && fresh.id === materialId ? fresh : (material ?? null)
    const awaiting = awaitsFigure(displayed)

    useEffect(() => {
        setFresh(null)
        setWaitOver(false)
        if (!awaitsFigure(material) || materialId == null) return
        let done = false
        const interval = setInterval(() => {
            apiCall<M>(dispatch, activitiesService.getMaterial as ApiService, { routeParams: { id: materialId } })
                .then((data) => {
                    if (done || data?.id !== materialId || !hasFigure(data)) return
                    setFresh(data)
                    stop()
                })
                // Une relecture ratée n'arrête pas l'attente : la suivante peut aboutir. Sans réponse
                // (hors ligne : statut 0 ou absent), un fil d'Ariane suffit — une erreur toutes les 5 s
                // noierait Bugsnag.
                .catch((error) => {
                    if (!httpStatusOf(error)) telemetry.transition("figure", "waiting", "offline")
                    else telemetry.error("figure_poll", error)
                    if (done || isRetryableFailure(error)) return
                    setWaitOver(true)
                    stop()
                })
        }, FIGURE_POLL_INTERVAL_MS)
        // Le temps écoulé laisse `done` ouvert : une relecture déjà partie peut encore apporter la figure.
        const timeout = setTimeout(() => {
            setWaitOver(true)
            clearInterval(interval)
        }, FIGURE_POLL_TIMEOUT_MS)
        const stop = () => {
            done = true
            clearInterval(interval)
            clearTimeout(timeout)
        }
        return stop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialId])

    return { material: displayed, figurePending: awaiting && !waitOver }
}
